import { createFileRoute, redirect } from '@tanstack/react-router'
import { SteeringPage } from '../../components/steering/SteeringPage'

export const Route = createFileRoute('/$org/steering')({
  component: Steering,
  // Data quality left this page for the Data section (spec 2026-08-24-03,
  // lot 10). Links that still ask for it here — the old footer strip, the
  // `?nature=data_consistency` bookmarks that predate the category axis — are
  // sent on rather than silently landing on Economics.
  beforeLoad: ({ params, search }) => {
    const asksForDataQuality =
      (search as { category?: string; nature?: string }).category === 'data_quality' ||
      (search as { nature?: string }).nature === 'data_consistency'
    if (asksForDataQuality) {
      const s = search as { subcategory?: string; status?: string; sort?: string; q?: string; selected?: string }
      throw redirect({
        to: '/$org/data/quality',
        params: { org: params.org },
        search: {
          subcategory: s.subcategory,
          selected: s.selected,
          status: s.status,
          sort: s.sort,
          q: s.q,
          page: undefined,
        },
        replace: true,
      })
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      // The three-category axis (spec 2026-08-24-03), plus data_quality.
      category: search.category ? String(search.category) : undefined,
      // 'list' | 'pipeline' — the claim pipeline is a view of Incidents.
      view: search.view ? String(search.view) : undefined,
      selected: search.selected ? String(search.selected) : undefined,
      // The in-category facet (spec 2026-08-24-03, lot 5). It replaced the
      // `domain` param, which could not be scoped to a category — a `?domain=`
      // bookmark keeps its category and simply loses the narrower filter.
      subcategory: search.subcategory ? String(search.subcategory) : undefined,
      // Scope the queue to one asset's whole branch — how the home page's
      // critical-asset rows hand off (spec 2026-08-24-03, lot 11).
      asset: search.asset ? String(search.asset) : undefined,
      // Sub-assets of the scoped asset are included unless `subtree=0`.
      subtree: search.subtree !== undefined && Number(search.subtree) === 0 ? 0 : undefined,
      // Due-date filter: gt30 | gt7 | overdue | overdue7 | overdue30.
      due: search.due ? String(search.due) : undefined,
      // Sub-tickets as rows (`children=1`); off, the queue lists dossiers
      // with their current step (spec 2026-10-27-19 § 6).
      // Optional keys (spread), so links to this page need not name them.
      ...(search.children !== undefined && Number(search.children) === 1 ? { children: 1 as const } : {}),
      // `due`: only the tickets whose follow-up date has passed.
      ...(search.follow_up === 'due' ? { follow_up: 'due' as const } : {}),
      // The sidebar badge's list (spec 2026-10-27-23): an RFC 3339 instant,
      // the "last seen" the badge counted from, kept after the visit moves
      // the marker so the list still shows what was counted.
      ...(typeof search.attention === 'string' && !Number.isNaN(Date.parse(search.attention))
        ? { attention: search.attention }
        : {}),
      status: search.status ? String(search.status) : undefined,
      sort: search.sort ? String(search.sort) : undefined,
      q: search.q ? String(search.q) : undefined,
      // A number, not a string: the router round-trips a numeric search value
      // as `page=2`, but quotes a string one as `page="2"`.
      page: search.page !== undefined ? Number(search.page) || undefined : undefined,
      // Legacy params, still read so old bookmarks land on the right category
      // (see LEGACY_NATURE_CATEGORY); never written back.
      tab: search.tab ? String(search.tab) : undefined,
      nature: search.nature ? String(search.nature) : undefined,
    }
  },
})

function Steering() {
  return <SteeringPage />
}
